import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import DemoForm from '@segment/templates/Forms/DemoForm'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  overline: `For Marketing Teams`,
  heading: `Know your customer, hit your goals`,
  description: `No cookies? No problem. Segment helps you collect valuable first-party data, reducing your customer acquisition cost and increasing return on ad spend by enabling better targeting and multichannel engagement.`,
}

const Hero: FC<PropsWithChildren<unknown>> = () => {
  return (
    <Wrapper>
      <HeroComponent variant="2Col">
        <HeroCopy>
          <CopyText.Overline tag="h5" variant="h5">
            {copy.overline}
          </CopyText.Overline>
          <CopyText.Heading variant="h1" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pLarge" tag="p">
            {copy.description}
          </CopyText.Description>
          <HeroRecipe.Form>
            <DemoForm
              data-hook="demoFormContainer"
              isProgressive={true}
              customFormIdentifier="site-marketing-demo"
              successMessageAppearance="twilioLight"
            />
          </HeroRecipe.Form>
        </HeroCopy>

        <HeroContent>
          <picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroImage role="presentation" width="640" height="512" src={heroPng2x} alt="" loading="lazy" />
          </picture>
        </HeroContent>
      </HeroComponent>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  overflow: hidden;
  position: relative;
  z-index: 0;
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const HeroComponent = styled(HeroBase)`
  position: relative;
  z-index: -1;
`

const HeroCopy = styled(HeroRecipe.Copy)`
  max-width: 470px;
`

const HeroContent = styled(HeroRecipe.Contents)`
  position: relative;

  /**
   * This pseudo element mockups space for absolutely positioned image
   * that needs to overflow outside of the content element.
   */
  &::after {
    content: '';
    display: block;
    height: 0;
    overflow: hidden;
    padding-top: 80%;
    position: relative;
    pointer-events: none;

    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding-top: 70%;
    }
  }
`

const HeroImage = styled(Image)`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: -1;
  transform: translate(-50%, -50%);
`

export default Hero
